import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  Button,
  Stack,
  Select,
  Checkbox,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  useToast,
  Card,
  CardHeader,
  CardBody,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Divider,
  Grid,
  GridItem,
  Badge,
  Flex,
  Spinner,
} from '@chakra-ui/react';
import { FiDownload, FiFileText, FiArchive } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import * as nominationService from '../../services/api/nominationService';
import reportService from '../../services/api/reportService';
import config from '../../config/config';
import { getNominationStatusColor, getNominationStatusLabel } from '../../utils/evaluationStatusUtils';

const Reports = () => {
  const { currentUser } = useAuth();
  const isCommittee = currentUser?.role === 'committee';

  // Get user data directly from localStorage for more reliable branch access
  const getUserDataFromStorage = () => {
    try {
      const userDataString = localStorage.getItem('userData');
      if (userDataString) {
        return JSON.parse(userDataString);
      }
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
    }
    return null;
  };

  const userDataFromStorage = getUserDataFromStorage();
  const toast = useToast();

  // State for filters
  const [branches, setBranches] = useState([]);
  const [centers, setCenters] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedCenter, setSelectedCenter] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  // State for report options
  const [includeEvaluation, setIncludeEvaluation] = useState(false);
  const [includeAttachmentA, setIncludeAttachmentA] = useState(false);
  const [includeAttachmentB, setIncludeAttachmentB] = useState(false);
  const [reportFormat, setReportFormat] = useState('pdf');

  // State for nominations
  const [nominations, setNominations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Loading states for downloads
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [downloadingNominationId, setDownloadingNominationId] = useState(null);

  // Use static configuration for branches and centers
  useEffect(() => {
    // Set branches and centers from static config
    setBranches(config.organization.branches);
    setCenters(config.organization.centers);

    // If committee user, set and lock branch to their assigned branch
    if (isCommittee) {
      // First try to get branch_code from localStorage (most reliable)
      if (userDataFromStorage?.branch_code) {
        console.log('Setting branch from localStorage branch_code:', userDataFromStorage.branch_code);
        setSelectedBranch(userDataFromStorage.branch_code);
      }
      // Then try from currentUser object
      else if (currentUser?.branch_code) {
        console.log('Setting branch from currentUser.branch_code:', currentUser.branch_code);
        setSelectedBranch(currentUser.branch_code);
      }
      // Log if we couldn't find the branch code
      else {
        console.warn('Could not find branch_code for committee user:', { 
          currentUser, 
          userDataFromStorage,
          'localStorage userData': localStorage.getItem('userData')
        });
      }
    }
  }, [isCommittee, currentUser, userDataFromStorage]);

  // Fetch nominations based on filters
  const fetchNominations = async () => {
    setLoading(true);
    setError(null);

    try {
      const filters = {
        branchId: selectedBranch || undefined,
        centerId: selectedCenter || undefined,
        status: selectedStatus || undefined
      };

      const nominations = await nominationService.getNominationsWithFilters(filters);
      setNominations(nominations);
    } catch (error) {
      console.error('Error fetching nominations:', error);
      setError('حدث خطأ أثناء تحميل المرشحين. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleBranchChange = (e) => {
    // Only allow branch change for admin users
    if (isCommittee) return;

    setSelectedBranch(e.target.value);
    setSelectedCenter('');
  };

  const handleCenterChange = (e) => {
    const val = e.target.value;
    console.debug('[Reports] Center changed ->', val);
    setSelectedCenter(val);
  };

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
  };

  // Apply filters
  const handleApplyFilters = () => {
    console.debug('[Reports] Applying filters', { selectedBranch, selectedCenter, selectedStatus });
    fetchNominations();
  };

  // Reset filters
  const handleResetFilters = () => {
    // For committee users, don't reset branch filter
    if (!isCommittee) {
      setSelectedBranch('');
    }
    setSelectedCenter('');
    setSelectedStatus('');
    setNominations([]);
  };

  // Download individual nomination report
  const downloadNominationReport = async (nominationId, candidateName) => {
    try {
      setDownloadingNominationId(nominationId);
      // Use reportService to download the nomination report
      const response = await reportService.downloadNominationReport(nominationId, {
        includeEvaluation,
        includeAttachmentA,
        includeAttachmentB,
        format: reportFormat
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { 
        type: reportFormat === 'pdf' ? 'application/pdf' : 'application/zip' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      // Set filename based on format
      const extension = reportFormat === 'pdf' ? 'pdf' : 'zip';
      link.download = `${candidateName.replace(/\s+/g, '_')}_report.${extension}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: 'تم تنزيل التقرير بنجاح',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error downloading report:', error);
      toast({
        title: 'حدث خطأ أثناء تنزيل التقرير',
        description: 'يرجى المحاولة مرة أخرى',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDownloadingNominationId(null);
    }
  };

  // Download bulk reports
  const downloadBulkReports = async () => {
    try {
      setBulkDownloading(true);
      // Use reportService to download bulk reports
      const filters = {
        branchId: selectedBranch || undefined,
        centerId: selectedCenter || undefined,
        status: selectedStatus || undefined
      };
      console.debug('[Reports] Bulk export filters', filters);

      const options = {
        includeEvaluation,
        includeAttachments: includeAttachmentA || includeAttachmentB,
        format: reportFormat
      };

      const response = await reportService.bulkExportReports(filters, options);

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { 
        type: reportFormat === 'pdf' ? 'application/pdf' : 'application/zip' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      // Set filename based on format and filters
      const extension = reportFormat === 'pdf' ? 'pdf' : 'zip';
      const locationName = selectedCenter ? 
        (centers.find(c => String(c.id) === String(selectedCenter))?.name || 'all').replace(/\s+/g, '_') : 
        selectedBranch ? 
          (branches.find(b => String(b.id) === String(selectedBranch))?.name || 'all').replace(/\s+/g, '_') : 
          'all';

      link.download = `${locationName}_nominations_${new Date().toISOString().split('T')[0]}.${extension}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: 'تم تنزيل التقارير بنجاح',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error downloading bulk reports:', error);
      toast({
        title: 'حدث خطأ أثناء تنزيل التقارير',
        description: 'يرجى المحاولة مرة أخرى',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setBulkDownloading(false);
    }
  };

  // Centralized helpers
  const getStatusColor = (status) => getNominationStatusColor(status);
  const getStatusDisplay = (status) => getNominationStatusLabel(status);

  return (
    <>
      {/* Full-screen loading overlay for report generation/download */}
      {(bulkDownloading || !!downloadingNominationId) && (
        <Box
          position="fixed"
          top={0}
          left={0}
          width="100vw"
          height="100vh"
          bg="blackAlpha.600"
          zIndex={2000}
        >
          <Flex direction="column" align="center" justify="center" height="100%" color="white">
            <Spinner size="xl" thickness="4px" color="white" mb={4} />
            <Text fontSize="lg" fontWeight="bold">
              {bulkDownloading
                ? (reportFormat === 'zip' ? 'جارٍ إنشاء الملفات وضغطها...' : 'جارٍ إنشاء ملف PDF جماعي...')
                : (reportFormat === 'zip' ? 'جارٍ إنشاء تقرير المرشح (ZIP)...' : 'جارٍ إنشاء تقرير المرشح (PDF)...')}
            </Text>
            <Text fontSize="sm" opacity={0.9} mt={2}>قد يستغرق ذلك بعض الوقت حسب عدد المرشحين وحجم الملفات.</Text>
          </Flex>
        </Box>
      )}

      <Box p={4}>
        <Heading mb={6}>التقارير</Heading>

        <Tabs variant="enclosed" colorScheme="primary" mb={6}>
          <TabList>
            <Tab>تقارير المرشحين</Tab>
            <Tab>تقارير إحصائية</Tab>
          </TabList>

          <TabPanels>
            {/* Nomination Reports Tab */}
            <TabPanel>
              <Stack spacing={6}>
                {/* Filters Card */}
                <Card>
                  <CardHeader>
                    <Heading size="md">تصفية المرشحين</Heading>
                  </CardHeader>
                  <CardBody>
                    <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4}>
                      <GridItem>
                        <FormControl>
                          <FormLabel>الفرع</FormLabel>
                          <Select
                            value={selectedBranch}
                            onChange={handleBranchChange}
                            placeholder="اختر الفرع"
                            isDisabled={isCommittee}
                          >
                            {branches.map((branch) => (
                              <option key={branch.id} value={branch.id}>
                                {branch.name}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </GridItem>

                      <GridItem>
                        <FormControl>
                          <FormLabel>المركز</FormLabel>
                          <Select
                            value={selectedCenter}
                            onChange={handleCenterChange}
                            placeholder="اختر المركز"
                            isDisabled={!selectedBranch}
                          >
                            {centers
                              .filter((center) => !selectedBranch || center.branchId === selectedBranch)
                              .map((center) => (
                                <option key={center.id} value={center.id}>
                                  {center.name}
                                </option>
                              ))}
                          </Select>
                        </FormControl>
                      </GridItem>

                      <GridItem>
                        <FormControl>
                          <FormLabel>الحالة</FormLabel>
                          <Select
                            value={selectedStatus}
                            onChange={handleStatusChange}
                            placeholder="اختر الحالة"
                          >
                            <option value="draft">مسودة</option>
                            <option value="submitted">تحت التقييم</option>
                            <option value="pending">في انتظار النتيجة</option>
                            <option value="shortlisted">قبول أولي</option>
                            <option value="accepted">مقبول</option>
                            <option value="rejected">مرفوض</option>
                            <option value="returned">عائد</option>
                          </Select>
                        </FormControl>
                      </GridItem>
                    </Grid>

                    <Flex mt={4} justifyContent="flex-end" gap={3}>
                      <Button
                        colorScheme="gray"
                        onClick={handleResetFilters}
                      >
                        إعادة تعيين
                      </Button>
                      <Button
                        colorScheme="primary"
                        onClick={handleApplyFilters}
                        isLoading={loading}
                      >
                        تطبيق
                      </Button>
                    </Flex>
                  </CardBody>
                </Card>

                {/* Report Options Card */}
                <Card>
                  <CardHeader>
                    <Heading size="md">خيارات التقرير</Heading>
                  </CardHeader>
                  <CardBody>
                    <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                      <GridItem>
                        <Stack spacing={4}>
                          <Heading size="sm">محتوى التقرير</Heading>
                          <Checkbox
                            isChecked={includeEvaluation}
                            onChange={(e) => setIncludeEvaluation(e.target.checked)}
                          >
                            تضمين تقرير التقييم
                          </Checkbox>
                          <Checkbox
                            isChecked={includeAttachmentA}
                            onChange={(e) => setIncludeAttachmentA(e.target.checked)}
                          >
                            تضمين السيرة الذاتية
                          </Checkbox>
                          <Checkbox
                            isChecked={includeAttachmentB}
                            onChange={(e) => setIncludeAttachmentB(e.target.checked)}
                          >
                            تضمين المسح الأمني
                          </Checkbox>
                        </Stack>
                      </GridItem>

                      <GridItem>
                        <Stack spacing={4}>
                          <Heading size="sm">تنسيق التقرير</Heading>
                          <FormControl>
                            <Select
                              value={reportFormat}
                              onChange={(e) => setReportFormat(e.target.value)}
                            >
                              <option value="pdf">ملف PDF واحد (مدمج)</option>
                              <option value="zip">ملفات منفصلة (مضغوطة)</option>
                            </Select>
                          </FormControl>
                        </Stack>
                      </GridItem>
                    </Grid>

                    {(includeAttachmentA || includeAttachmentB) && (
                      <Alert status="info" mt={4}>
                        <AlertIcon />
                        <Text>
                          سيتم تضمين المرفقات المتاحة فقط. إذا لم تكن المرفقات متوفرة لمرشح معين، فلن يتم تضمينها في التقرير.
                        </Text>
                      </Alert>
                    )}
                  </CardBody>
                </Card>

                {/* Bulk Export Card */}
                {currentUser && currentUser.role === 'admin' && (
                  <Card>
                    <CardHeader>
                      <Heading size="md">تصدير جماعي</Heading>
                    </CardHeader>
                    <CardBody>
                      <Text mb={4}>
                        يمكنك تصدير تقارير لجميع المرشحين المحددين في التصفية أعلاه.
                        {!selectedBranch && !selectedCenter && !selectedStatus && (
                          ' سيتم تصدير تقارير لجميع المرشحين.'
                        )}
                      </Text>

                      <Button
                        leftIcon={<FiArchive />}
                        colorScheme="primary"
                        onClick={downloadBulkReports}
                        isDisabled={loading || bulkDownloading}
                        isLoading={bulkDownloading}
                        loadingText={reportFormat === 'zip' ? 'جارٍ إنشاء الملفات وضغطها...' : 'جارٍ إنشاء ملف PDF...'}
                      >
                        تصدير جماعي للتقارير
                      </Button>
                    </CardBody>
                  </Card>
                )}

                {/* Nominations List */}
                <Card>
                  <CardHeader>
                    <Heading size="md">قائمة المرشحين</Heading>
                  </CardHeader>
                  <CardBody>
                    {error && (
                      <Alert status="error" mb={4}>
                        <AlertIcon />
                        {error}
                      </Alert>
                    )}

                    {loading ? (
                      <Flex justify="center" align="center" py={10}>
                        <Spinner size="xl" color="primary.500" />
                      </Flex>
                    ) : nominations.length > 0 ? (
                      <Stack spacing={4} divider={<Divider />}>
                        {nominations.map((nomination, idx) => {
                          // Build candidate name robustly against various API shapes
                          const nameFromFields = [nomination?.first_name, nomination?.father_name, nomination?.last_name]
                            .filter(Boolean)
                            .join(' ')
                            .trim();
                          const candidateName = nomination?.candidate?.full_name
                            ? nomination.candidate.full_name
                            : (nameFromFields || nomination?.full_name || nomination?.applicant_name || 'غير معروف');
                          const applicationNumber = nomination?.application_number || nomination?.id || '-';
                          return (
                            <Flex
                              key={nomination?.id ?? idx}
                              justify="space-between"
                              align="center"
                              p={2}
                            >
                              <Box>
                                <Text fontWeight="bold">{candidateName}</Text>
                                <Text fontSize="sm" color="gray.600">
                                  رقم الطلب: {applicationNumber}
                                </Text>
                                <Badge colorScheme={getStatusColor(nomination?.status)}>
                                  {getStatusDisplay(nomination?.status)}
                                </Badge>
                              </Box>

                              <Button
                                leftIcon={<FiDownload />}
                                colorScheme="primary"
                                size="sm"
                                isLoading={downloadingNominationId === nomination?.id}
                                loadingText={reportFormat === 'zip' ? 'تحميل ZIP...' : 'تحميل PDF...'}
                                isDisabled={downloadingNominationId && downloadingNominationId !== nomination?.id}
                                onClick={() => downloadNominationReport(nomination?.id, candidateName)}
                              >
                                تنزيل التقرير
                              </Button>
                            </Flex>
                          );
                        })}
                      </Stack>
                    ) : (
                      <Text textAlign="center" py={10} color="gray.500">
                        قم بتطبيق التصفية لعرض المرشحين
                      </Text>
                    )}
                  </CardBody>
                </Card>
              </Stack>
            </TabPanel>

            {/* Statistical Reports Tab */}
            <TabPanel>
              <Card>
                <CardHeader>
                  <Heading size="md">التقارير الإحصائية</Heading>
                </CardHeader>
                <CardBody>
                  <Text mb={4}>
                    يمكنك تنزيل التقارير الإحصائية للمرشحين والتقييمات.
                  </Text>

                  <Stack spacing={4} direction={{ base: "column", md: "row" }}>
                    <Button
                      leftIcon={<FiFileText />}
                      colorScheme="primary"
                      onClick={() => {
                        let url = '/reports/statistics/nominations';
                        if (selectedBranch) url += `?branch_id=${selectedBranch}`;
                        if (selectedCenter) url += `${selectedBranch ? '&' : '?'}center_id=${selectedCenter}`;

                        window.open(url, '_blank');
                      }}
                    >
                      إحصائيات المرشحين
                    </Button>

                    <Button
                      leftIcon={<FiFileText />}
                      colorScheme="primary"
                      onClick={() => {
                        let url = '/reports/statistics/evaluations';
                        if (selectedBranch) url += `?branch_id=${selectedBranch}`;
                        if (selectedCenter) url += `${selectedBranch ? '&' : '?'}center_id=${selectedCenter}`;

                        window.open(url, '_blank');
                      }}
                    >
                      إحصائيات التقييمات
                    </Button>
                  </Stack>
                </CardBody>
              </Card>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </>
  );
};

export default Reports;
